import {
  useQuery
} from "@tanstack/react-query";

import {
  Leaf
} from "lucide-react";

import {
  getFeaturedIngredients,
  getPublicIngredientImageUrl
} from "../../services/catalog";

import {
  findHomeSection,
  useHomeSections
} from "./HomeSectionCms";

export function CmsIngredientSection() {
  const sectionsQuery =
    useHomeSections();

  const ingredientsQuery =
    useQuery({
      queryKey: [
        "homepage",
        "ingredients"
      ],

      queryFn:
        getFeaturedIngredients,

      staleTime: 0,

      refetchOnMount:
        "always",

      refetchOnWindowFocus:
        true
    });

  const section =
    findHomeSection(
      sectionsQuery.data,
      "INGREDIENTS"
    );

  if (
    section &&
    !section.isVisible
  ) {
    return null;
  }

  const ingredients =
    ingredientsQuery.data ??
    [];

  if (
    !ingredientsQuery.isLoading &&
    ingredients.length === 0
  ) {
    return null;
  }

  const eyebrow =
    section?.eyebrow ??
    "Ingredient stories";

  const title =
    section?.title ??
    "Ingredients with purpose";

  const subtitle =
    section?.subtitle ??
    "Discover the botanical ingredients and thoughtful choices behind RiNi bodycare.";

  return (
    <section className="rini-section bg-[#edf1e8]">
      <div className="rini-container">
        <div className="mx-auto max-w-3xl text-center">
          {eyebrow && (
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
              {eyebrow}
            </p>
          )}

          <h2 className="rini-heading mt-4 text-4xl font-semibold sm:text-5xl">
            {title}
          </h2>

          {subtitle && (
            <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-[#746d65]">
              {subtitle}
            </p>
          )}
        </div>

        {ingredientsQuery.isLoading ? (
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({
              length: 4
            }).map(
              (_item, index) => (
                <div
                  key={index}
                  className="h-96 animate-pulse rounded-[2rem] bg-white/60"
                />
              )
            )}
          </div>
        ) : (
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {ingredients.map(
              (
                ingredient,
                index
              ) => (
                <article
                  key={
                    ingredient.id
                  }
                  className="overflow-hidden rounded-[2rem] bg-white shadow-sm"
                >
                  {ingredient.imageKey ? (
                    <img
                      src={
                        getPublicIngredientImageUrl(
                          ingredient.id,
                          ingredient.imageKey
                        )
                      }
                      alt={
                        ingredient.name
                      }
                      loading="lazy"
                      className="aspect-square w-full object-cover"
                    />
                  ) : (
                    <div className="flex aspect-square items-center justify-center bg-[#dfe6d5]">
                      <Leaf
                        size={42}
                        className="text-[#70845b]"
                        strokeWidth={
                          1.5
                        }
                      />
                    </div>
                  )}

                  <div className="p-6 text-center">
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#a27a4d]">
                      {ingredient.shortTitle ??
                        `Botanical ${String(
                          index + 1
                        ).padStart(
                          2,
                          "0"
                        )}`}
                    </p>

                    <h3 className="mt-2 font-serif text-2xl text-[#38251e]">
                      {
                        ingredient.name
                      }
                    </h3>

                    {ingredient.description && (
                      <p className="mt-3 leading-7 text-[#746d65]">
                        {
                          ingredient.description
                        }
                      </p>
                    )}
                  </div>
                </article>
              )
            )}
          </div>
        )}
      </div>
    </section>
  );
}
